import { useState, type ReactNode } from 'react';
import { Label, Radio } from '@shared/components';
import { useArrowKeyReveal } from '../hooks/useArrowKeyReveal';

export interface PaymentMethodOption {
    value: string;
    label: string;
    disabledReason?: string;
    renderForm: (autoFocus: boolean) => ReactNode;
}

interface PaymentMethodSlotProps {
    options: ReadonlyArray<PaymentMethodOption>;
    autoFocus: boolean;
    locked: boolean;
    initialSelected?: string;
}

const GROUP_LABEL_ID = 'invoice-payment-method-label';
const RADIO_NAME = 'invoice-payment-method';

export function PaymentMethodSlot({ options, autoFocus, locked, initialSelected }: PaymentMethodSlotProps) {
    const [selected, setSelected] = useState<string | null>(initialSelected ?? null);
    const reveal = useArrowKeyReveal(setSelected);

    if (options.length === 1) {
        const [only] = options;
        return (
            <div className="space-y-3">
                <p className="text-sm font-medium text-mp-foreground">{only.label}</p>
                {only.renderForm(autoFocus)}
            </div>
        );
    }

    const chosen = options.find((option) => option.value === selected && !option.disabledReason);

    return (
        <div className="space-y-3">
            <div
                role="radiogroup"
                aria-labelledby={GROUP_LABEL_ID}
                className="space-y-2"
                onKeyDown={reveal.onKeyDown}
                onKeyUp={reveal.onKeyUp}
            >
                <Label id={GROUP_LABEL_ID}>Who is taking the payment?</Label>
                {options.map((option) => {
                    const radioId = `${RADIO_NAME}-${option.value}`;
                    const reasonId = `${radioId}-reason`;
                    return (
                        <div key={option.value} className="space-y-1">
                            <Radio
                                id={radioId}
                                name={RADIO_NAME}
                                value={option.value}
                                label={option.label}
                                checked={selected === option.value && !option.disabledReason}
                                disabled={locked || !!option.disabledReason}
                                ariaDescribedBy={option.disabledReason ? reasonId : undefined}
                                onChange={() => reveal.choose(option.value)}
                            />
                            {option.disabledReason && (
                                <p id={reasonId} className="pl-8 text-sm text-mp-muted">
                                    {option.disabledReason}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
            {chosen?.renderForm(reveal.focusOnReveal)}
        </div>
    );
}
